/**
 * "Save the Admiral" campaign — the pure state machine (resistance, sway, renown).
 *
 * The Admiral's mind is lost in an inner sea of endless naval battles. The
 * player sways every state of every Region to bring him home. Ported from the
 * legacy v2 build (legacy/admirals-return-godot/scripts/main.gd CAMPAIGN
 * section), with the rules traced to docs/reference/site/manual/sta-map.md:
 *
 *  - Win a state's special battle → it is *swayed*; every other non-swayed
 *    state in the Region drops one resistance (reaching 0 sways it without a
 *    fight). (manual: "reduce the resistance of all adjacent non-swayed states
 *    by one. Reducing a state's resistance to zero this way awards the state".)
 *  - A state with resistance 8+ is "swayed against" — a win only *softens* it
 *    (−2) rather than swaying it. (manual: "high resistance … shown with a Red
 *    X … winning only reduces its overall resistance".)
 *  - Lose → the state resents it: +2 resistance here, +1 on adjacent
 *    non-swayed states. (manual: "resistance usually increases by 2 and
 *    adjacent non-swayed states by 1".)
 *  - Sway every state in a Region to unlock the next; sway all to bring the
 *    Admiral home.
 *
 * Renown ("State points" in the manual) is earned every battle and funds the
 * player's fleet budget in the next state battle. Deeper reward mechanics
 * (flagship modules, one-use ability plays, the "Influence" Region effect) are
 * flavour here and tracked as renown only — they land with the STA-advanced
 * stretch work (WP-13). Each region's three states are treated as mutually
 * adjacent (a region is one political cluster), matching the legacy build.
 *
 * Pure: no UI, no DOM, no RNG, no wall-clock — safe to unit-test as arithmetic.
 */
import type { MapSize, Side } from './state';

/** Persisted campaign progress. `res`/`swayed` are keyed by `region-state`. */
export interface Campaign {
  /** state key → swayed to the Admiral's cause */
  swayed: Record<string, boolean>;
  /** state key → current resistance (absent = its base resistance) */
  res: Record<string, number>;
  /** renown points ("State points") accumulated across battles */
  points: number;
}

export interface StateDef {
  name: string;
  /** short initials shown on the region map */
  code: string;
  /** the reward the state offers when swayed (flavour; mechanically = renown) */
  reward: string;
}

export interface RegionDef {
  name: string;
  /** battle map size for this region's state battles */
  size: MapSize;
  states: StateDef[];
}

/** Three Regions × three states = the nine-state campaign (legacy v2 names). */
export const CAMP_REGIONS: RegionDef[] = [
  {
    name: 'Coastal Provinces',
    size: 'small',
    states: [
      { name: 'Avalon', code: 'AV', reward: '+6 State points' },
      { name: 'Brightwater', code: 'BW', reward: 'Spy — reveal the enemy fleet' },
      { name: 'Caldera', code: 'CA', reward: 'Flagship module “ABBC”' },
    ],
  },
  {
    name: 'The Middle Seas',
    size: 'medium',
    states: [
      { name: 'Dunmore', code: 'DU', reward: 'Call for Reinforcements' },
      { name: 'Eastgate', code: 'EG', reward: '+8 Bribe points' },
      { name: 'Frostholm', code: 'FH', reward: 'Sabotage — freeze a fleet a turn' },
    ],
  },
  {
    name: "Admiral's Reach",
    size: 'large',
    states: [
      { name: 'Grimsby Deep', code: 'GD', reward: 'Influence — region-wide sway' },
      { name: 'Highmarch', code: 'HM', reward: 'Mutiny — turn enemy ships' },
      { name: 'Ironreach', code: 'IR', reward: 'Flagship module “CDDC”' },
    ],
  },
];

export const STATES_PER_REGION = 3;
/** total states across all regions */
export const TOTAL_STATES = CAMP_REGIONS.length * STATES_PER_REGION;
/** resistance at or above which a state is "swayed against" (manual: usually 8+) */
export const AGAINST_THRESHOLD = 8;

export function newCampaign(): Campaign {
  return { swayed: {}, res: {}, points: 0 };
}

export function stateKey(region: number, state: number): string {
  return `${region}-${state}`;
}

/** A small clone so applyBattleResult can stay non-mutating (pure). */
function cloneCampaign(c: Campaign): Campaign {
  return { swayed: { ...c.swayed }, res: { ...c.res }, points: c.points };
}

/**
 * The state's untouched resistance, rising across the campaign so later states
 * are tougher (legacy: 2 + floor((region*3 + state) / 2)).
 */
export function baseResistance(region: number, state: number): number {
  return 2 + Math.floor((region * STATES_PER_REGION + state) / 2);
}

export function stateResistance(camp: Campaign, region: number, state: number): number {
  const k = stateKey(region, state);
  return k in camp.res ? camp.res[k]! : baseResistance(region, state);
}

export function isSwayed(camp: Campaign, region: number, state: number): boolean {
  return camp.swayed[stateKey(region, state)] === true;
}

/** Resistance 8+ → "swayed against": a win softens rather than sways. */
export function isAgainst(camp: Campaign, region: number, state: number): boolean {
  return !isSwayed(camp, region, state) && stateResistance(camp, region, state) >= AGAINST_THRESHOLD;
}

/** Region 0 is open; later Regions unlock once every state of the prior is swayed. */
export function regionUnlocked(camp: Campaign, region: number): boolean {
  if (region <= 0) return true;
  for (let s = 0; s < STATES_PER_REGION; s++) {
    if (!isSwayed(camp, region - 1, s)) return false;
  }
  return true;
}

export function swayedCount(camp: Campaign): number {
  let n = 0;
  for (let r = 0; r < CAMP_REGIONS.length; r++) {
    for (let s = 0; s < STATES_PER_REGION; s++) if (isSwayed(camp, r, s)) n++;
  }
  return n;
}

export function campaignComplete(camp: Campaign): boolean {
  return swayedCount(camp) >= TOTAL_STATES;
}

/** The Red fleet budget renown buys for the next state battle (legacy cap +80). */
export function renownBudget(points: number): number {
  return 200 + Math.min(80, Math.max(0, points));
}

export interface BattleConfig {
  size: MapSize;
  budgetR: number;
  budgetY: number;
  /** required win margin (%) the player (favoured Red) must clear */
  ratio: number;
  favored: Side;
  /** number of "resistance aspects" the battle carries (manual: harder = more) */
  aspects: number;
  /** the state's global index region*3+state (drives difficulty scaling) */
  gIdx: number;
}

/**
 * The battle parameters a state demands, scaling with its resistance and how
 * deep into the campaign it sits (legacy start_campaign_battle):
 *   aspects = max(currentRes, ceil(baseRes / 2))
 *   ratio   = max(10, 10 + 5*gIdx + 5*aspects)
 *   enemy $ = 200 + 20*gIdx + 10*aspects ; your $ = renown-funded.
 */
export function battleConfig(camp: Campaign, region: number, state: number): BattleConfig {
  const gIdx = region * STATES_PER_REGION + state;
  const res = stateResistance(camp, region, state);
  const aspects = Math.max(res, Math.ceil(baseResistance(region, state) / 2));
  const ratio = Math.max(10, 10 + 5 * gIdx + 5 * aspects);
  return {
    size: CAMP_REGIONS[region]!.size,
    budgetR: renownBudget(camp.points),
    budgetY: 200 + 20 * gIdx + 10 * aspects,
    ratio,
    favored: 'R',
    aspects,
    gIdx,
  };
}

/** The over-win margin (%) beyond the required ratio, given final VPs. */
export function overWinPct(vpR: number, vpY: number, ratio: number): number {
  return Math.round((vpR / Math.max(1, vpY) - 1) * 100) - ratio;
}

export interface CampaignResult {
  /** the new campaign progress (input is never mutated) */
  campaign: Campaign;
  /** human-readable summary for the game-over screen */
  message: string;
  renownGained: number;
  /** the chosen state was swayed this battle */
  swayed: boolean;
  /** a win on a "swayed against" state that only softened its resistance */
  softened: boolean;
  /** names of adjacent states swayed without a fight (resistance hit 0) */
  autoSwayed: string[];
}

/**
 * Apply a finished state battle to the campaign. Pure: returns fresh progress.
 *
 *   WIN  → +renown (15 + 5 per full 10% over-win). If the state was swayed
 *          against (res ≥ 8) it only softens (−2); otherwise it is swayed and
 *          every other non-swayed state in the Region drops 1 resistance
 *          (0 ⇒ auto-swayed).
 *   LOSE → +4 renown; this state +2 resistance, the rest of the Region +1.
 */
export function applyBattleResult(
  camp: Campaign,
  region: number,
  state: number,
  outcome: { youWon: boolean; vpR: number; vpY: number },
): CampaignResult {
  const next = cloneCampaign(camp);
  const k = stateKey(region, state);
  const def = CAMP_REGIONS[region]!.states[state]!;
  const cfg = battleConfig(camp, region, state);
  const autoSwayed: string[] = [];
  let swayed = false;
  let softened = false;
  let renownGained: number;
  let message: string;

  if (outcome.youWon) {
    const over = overWinPct(outcome.vpR, outcome.vpY, cfg.ratio);
    renownGained = 15 + Math.max(0, Math.floor(over / 10) * 5);
    next.points += renownGained;
    const res = stateResistance(camp, region, state);
    if (res >= AGAINST_THRESHOLD) {
      next.res[k] = res - 2;
      softened = true;
      message = `${def.name} was swayed against the Admiral — your victory softens its `
        + `resistance to ${res - 2}, but it is not yet won. +${renownGained} renown.`;
    } else {
      next.swayed[k] = true;
      swayed = true;
      for (let s2 = 0; s2 < STATES_PER_REGION; s2++) {
        if (s2 === state) continue;
        const k2 = stateKey(region, s2);
        if (next.swayed[k2]) continue;
        const lowered = Math.max(0, stateResistance(next, region, s2) - 1);
        next.res[k2] = lowered;
        if (lowered === 0) {
          next.swayed[k2] = true;
          autoSwayed.push(CAMP_REGIONS[region]!.states[s2]!.name);
        }
      }
      message = `${def.name} is swayed to the Admiral's cause! +${renownGained} renown. `
        + 'Adjacent states lose 1 resistance.';
      if (autoSwayed.length) message += ` ${autoSwayed.join(', ')} swayed without a fight!`;
    }
  } else {
    renownGained = 4;
    next.points += renownGained;
    next.res[k] = stateResistance(camp, region, state) + 2;
    for (let s2 = 0; s2 < STATES_PER_REGION; s2++) {
      if (s2 === state) continue;
      const k2 = stateKey(region, s2);
      if (next.swayed[k2]) continue;
      next.res[k2] = stateResistance(next, region, s2) + 1;
    }
    message = `${def.name} resents the failure — its resistance rises by 2 (adjacent +1). `
      + `+${renownGained} renown for the effort.`;
  }

  return { campaign: next, message, renownGained, swayed, softened, autoSwayed };
}
